import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 md:pt-18">
          <div className="w-full px-4 md:px-6 py-20 text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-file-search-line text-2xl"></i>
            </div>
            <h1 className="text-xl font-semibold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-600 mb-6">您访问的文章不存在或已被移除</p>
            <Link
              to="/articles"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-all whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回健康百科
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Organization',
      name: article.author
    },
    datePublished: article.date,
    image: article.coverImage,
    publisher: {
      '@type': 'Organization',
      name: '爱液官网',
      url: 'https://www.aiye-official.com/'
    }
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 爱液官网健康百科`}
        description={article.excerpt}
        keywords={`爱液官网,${article.category},润滑液,私密健康,${article.title}`}
        canonicalUrl={`/articles/${article.slug}`}
        ogType="article"
        structuredData={articleSchema}
      />

      <Navbar />

      <main className="pt-16 md:pt-18">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: '健康百科', to: '/articles' },
            { label: article.title }
          ]}
        />

        <article className="w-full bg-background-50">
          {/* Article Header */}
          <div className="w-full px-4 md:px-6 py-8 md:py-12">
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full">
                  {article.category}
                </span>
                <span className="text-xs text-foreground-500">{article.readTime}</span>
              </div>
              <h1 className="text-2xl md:text-4xl font-heading font-semibold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-500">
                <span>{article.author}</span>
                <span>·</span>
                <span>{article.date}</span>
              </div>
            </div>
          </div>

          {/* Cover Image */}
          <div className="w-full px-4 md:px-6 mb-8 md:mb-12">
            <div className="max-w-4xl mx-auto aspect-[16/9] rounded-2xl overflow-hidden bg-background-100">
              <img
                src={article.coverImage}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Article Content */}
          <div className="w-full px-4 md:px-6 pb-16 md:pb-20">
            <div className="max-w-3xl mx-auto">
              <div className="prose prose-lg max-w-none">
                {article.content.map((block, index) => {
                  if (block.type === 'h2') {
                    return (
                      <h2 key={index} className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mt-10 mb-4">
                        {block.text}
                      </h2>
                    );
                  }
                  if (block.type === 'h3') {
                    return (
                      <h3 key={index} className="text-lg md:text-xl font-heading font-semibold text-foreground-800 mt-8 mb-3">
                        {block.text}
                      </h3>
                    );
                  }
                  return (
                    <p key={index} className="text-base leading-relaxed text-foreground-700 mb-5">
                      {block.text}
                    </p>
                  );
                })}
              </div>

              {/* Back to list */}
              <div className="mt-12 pt-8 border-t border-background-200">
                <Link
                  to="/articles"
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回健康百科
                </Link>
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}